import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Grid, IconButton, Stack, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileRenameOutlineRoundedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import moment from 'moment';
import { onSocketEvent } from '../../utils/socketService';
import { disableScoreControls, enableScoreControls } from '../../features/GlobalState/globalState.slice';
import { getSiteConfiguration } from '../../features/Configuration/configurationAction';
import { openSnackbar } from '../../features/SnackBar/snackBarSlice';
import {
  deleteHealthScore,
  getHealthInsightsList,
  updateHealthScore,
} from '../../features/HealthScore/HealthScoreAction';
import usePackageSelection from '../../modules/Accounts/components/PackageSelection/usePackageSelection';
import { resetAccountsList } from '../../features/AccountList/accountListSlice';

export function LabelCell(props) {
  const { isPrimary, label } = props;
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  return isSmallScreen ? (
    <Stack direction={isSmallScreen ? 'column' : 'row'}>
      <Box width="100%" display="flex" alignItems="center">
        <Tooltip title={label}>
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginRight: '8px',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        {isPrimary && (
          <Typography
            sx={{
              flexShrink: 0,
              borderRadius: '5px',
              backgroundColor: '#D8FDD7',
              padding: '0px 6px',
              color: '#35B430',
              ...(isSmallScreen && {
                fontSize: '12px',
              }),
            }}
          >
            {isSmallScreen ? 'Primary' : 'Primary KPI Score'}
          </Typography>
        )}
      </Box>
    </Stack>
  ) : (
    <Box width="100%">
      <Box display="flex" alignItems="center" width="100%">
        <Tooltip title={label}>
          <Typography
            sx={{
              width: 'auto',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginRight: '8px',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        {isPrimary && (
          <Typography
            style={{
              borderRadius: '5px',
              backgroundColor: '#D8FDD7',
              padding: '0px 6px',
              color: '#35B430',
            }}
          >
            Primary KPI Score
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ActionCell({
  actionCellData, params, healthScoreList, scoreControlsEnabled,
}) {
  return (
    <Grid container spacing={2} justifyContent="center">
      {actionCellData.length
        && actionCellData.map((obj) => {
          const Icon = obj.icon;
          const isDisabled = (obj.id !== 'edit' && params.row.primaryHealthScore)
          || (obj.id === 'copy' && (healthScoreList?.length >= 5 || !scoreControlsEnabled))
          || ((obj.id === 'make_primary' || obj.id === 'delete') && !scoreControlsEnabled);
          return (
            <Grid item>
              {obj.id === 'make_primary' ? (
                <Button
                  className={obj.headerClassName}
                  key={obj.id}
                  variant="outlined"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => obj.handleFunction(params.row.healthScoreId)}
                >
                  {obj.name}
                </Button>
              ) : (
                <Tooltip title={obj.name}>
                  <IconButton
                    className={obj.headerClassName}
                    variant="outlined"
                    disabled={isDisabled}
                    onClick={() => obj.handleFunction(params.row.healthScoreId)}
                  >
                    <Icon color={isDisabled ? 'disabled' : 'primary'} fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          );
        })}
    </Grid>
  );
}

const useHealthScoreConfig = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [makePrimary, setMakePrimary] = useState();
  const [actionType, setActionType] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleEditClick = (id) => {
    navigate(`/kpi-score-configuration/update-health-score/${id}`);
  };

  const { activePackage } = usePackageSelection();
  const [healthScoreListData, setHealthScoreListData] = useState([]);
  const scoreControlsEnabled = useSelector((state) => state.scoreControls?.enabled);
  const siteConfiguration = useSelector((state) => state.siteConfiguration?.data);
  const healthInsights = useSelector((state) => state.healthInsights);
  const { healthInsightsList } = healthInsights;

  useEffect(() => {
    dispatch(getHealthInsightsList());
    dispatch(getSiteConfiguration());
  }, []);

  useEffect(() => {
    if (siteConfiguration?.scoreControlsEnabled === true) {
      dispatch(enableScoreControls());
    } else if (siteConfiguration?.scoreControlsEnabled === false) {
      dispatch(disableScoreControls());
    }
  }, [siteConfiguration?.scoreControlsEnabled]);

  const handleCopyClick = (id) => {
    navigate(`/kpi-score-configuration/update-health-score/${id}`, {
      state: { isCopyClick: true },
    });
  };

  useEffect(() => {
    onSocketEvent('kpiScoreUpdate', (data) => {
      if (data.packageId === activePackage?.packageId) {
        if (data.status === 'PENDING') {
          dispatch(disableScoreControls());
        } else {
          dispatch(enableScoreControls());
          dispatch(resetAccountsList());
        }
      }
    });
  }, [activePackage?.packageId]);

  const handleMakePrimaryClick = async (id) => {
    const data = {
      healthScoreId: id,
      primaryHealthScore: true,
    };
    setOpenDialog(true);
    setMakePrimary(data);
    setActionType('makePrimary');
  };
  const handleMakePrimaryConfirm = useCallback(() => {
    dispatch(updateHealthScore({ data: makePrimary })).then((res) => {
      if (res.error) {
        dispatch(openSnackbar({ open: true, message: res.payload, type: 'error' }));
      }
    });
    setOpenDialog(false);
  }, [makePrimary]);

  const handleDeleteConfirm = useCallback(() => {
    dispatch(deleteHealthScore(deleteId)).then((res) => {
      if (res.error) {
        dispatch(openSnackbar({ open: true, message: res.payload, type: 'error' }));
      }
    });
    setOpenDialog(false);
  }, [deleteId]);
  const handleDelete = async (id) => {
    setOpenDialog(true);
    setDeleteId(id);
    setActionType('delete');
  };

  const handleAddNewScore = () => {
    navigate('/kpi-score-configuration/new-health-score');
  };

  useEffect(() => {
    setHealthScoreListData(healthInsightsList);
  }, [healthInsightsList]);

  const healthScoreList = healthScoreListData
    .filter((item) => item && item.healthScoreLabel)
    .sort((a, b) => a.healthScoreLabel.toLowerCase().localeCompare(b.healthScoreLabel.toLowerCase()));

  const actionCellData = [
    {
      id: 'make_primary',
      name: 'Make Primary',
      disabled: false,
      icon: AutoAwesomeIcon,
      handleFunction: handleMakePrimaryClick,
      headerClassName: 'tg-kpi-score-primary',
    },
    {
      id: 'copy',
      name: 'Copy',
      icon: ContentCopyIcon,
      disabled: false,
      handleFunction: handleCopyClick,
      headerClassName: 'tg-kpi-score-copy',
    },
    {
      id: 'edit',
      name: 'Edit',
      icon: DriveFileRenameOutlineRoundedIcon,
      disabled: false,
      handleFunction: handleEditClick,
      headerClassName: 'tg-kpi-score-edit',
    },
    {
      id: 'delete',
      name: 'Delete',
      icon: DeleteIcon,
      disabled: false,
      handleFunction: handleDelete,
      headerClassName: 'tg-kpi-score-delete',
    },
  ];
  const HealthScoreColumns = [
    {
      field: 'healthScoreLabel',
      headerName: 'Label',
      minWidth: 300,
      flex: 1,
      sortable: true,
      renderCell: (params) => <LabelCell mutedText label={params.row.healthScoreLabel} isPrimary={params.row.primaryHealthScore} />,
    },
    {
      field: 'lastModified',
      headerName: 'Last Modified',
      width: 60,
      minWidth: 100,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'center-align--header',
      sortable: true,
      renderCell: (params) => moment(params.value).fromNow(),
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      width: 450,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => <ActionCell actionCellData={actionCellData} params={params} healthScoreList={healthScoreList} scoreControlsEnabled={scoreControlsEnabled} />,
    },
  ];
  const handleClose = () => {
    setOpenDialog(false);
  };

  return {
    scoreControlsEnabled,
    handleMakePrimaryClick,
    handleDelete,
    healthScoreList,
    handleAddNewScore,
    handleEditClick,
    HealthScoreColumns,
    openDialog,
    handleMakePrimaryConfirm,
    actionType,
    handleClose,
    handleDeleteConfirm,
    loading: healthInsights.loading,
    data: healthInsightsList,
    error: healthInsights.error,
  };
};

export default useHealthScoreConfig;
